// Reusable PDF thumbnail — renders page 1 of a PDF as a small bitmap image.
// Wired into attachment lists across the app (quote PDFs, drawing PDFs,
// customer-uploaded RFQ PDFs, etc.) in a follow-up pass.
//
// Design notes:
//   - pdfjs-dist is HEAVY (~1MB raw, ~350KB gzipped). We lazy-import it inside
//     a useEffect so it never lands in the main bundle. The first thumbnail
//     on the page pays the cost; subsequent ones reuse the cached module.
//   - pdfjs-dist 5.x requires a worker URL. We use Vite's recommended
//     `new URL(..., import.meta.url)` pattern against the package's exported
//     `build/pdf.worker.mjs` — Vite resolves and fingerprints it as an asset
//     at build time, and serves it directly in dev. This avoids the older
//     CDN-string-version-pinning approach (brittle: version drift) and the
//     `?worker&url` Vite suffix approach (doesn't play nice with third-party
//     ESM worker entries). The worker URL is set once globally on
//     GlobalWorkerOptions and re-setting it is a no-op.
//   - We accept Blob OR URL string. For Blob we read arrayBuffer() and pass
//     it directly to getDocument — no createObjectURL needed (cleaner, no
//     leak surface). For URL strings, getDocument fetches over the network.
//   - Render goes to an offscreen <canvas>, displayed via that same canvas
//     in the DOM (no need to roundtrip through toDataURL — wasted bytes).
//   - Loading state: a gray skeleton at letter aspect (1.294). Error state:
//     red-bordered placeholder with a "?" glyph.
//
// Does NOT integrate with anything yet — pure presentational primitive.

import { useEffect, useRef, useState } from 'react';
import { ref as storageRef, getBlob } from 'firebase/storage';
import { storage } from '@/shared/firebase';

export interface PdfThumbnailProps {
  // EITHER a Blob (just-generated PDF) OR a URL (saved attachment).
  source: Blob | string;
  // Width in CSS pixels; height is derived from the PDF page aspect ratio.
  width?: number;
  // Optional click handler (e.g. open in PdfViewerModal).
  onClick?: () => void;
  // Optional className for outer wrapper (border, hover state, etc.).
  className?: string;
  // Optional label rendered below the image (e.g. "Quote Q-1042.pdf").
  label?: string;
}

// US letter page aspect ratio (11 / 8.5). Used for the skeleton placeholder
// before we know the real page dimensions.
const LETTER_ASPECT = 11 / 8.5;

// Track whether we've initialized the worker globally — pdfjs-dist tolerates
// re-assignment but it's wasteful to keep doing it on every mount.
let workerInitialized = false;

export function PdfThumbnail({
  source,
  width = 96,
  onClick,
  className,
  label,
}: PdfThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  // Track real rendered height so the wrapper sizes to the actual aspect ratio
  // after render completes (rather than stuck at the letter-default skeleton).
  const [renderedHeight, setRenderedHeight] = useState<number>(
    Math.round(width * LETTER_ASPECT),
  );

  useEffect(() => {
    let cancelled = false;
    // Blob URL guard — we don't create one today (we use arrayBuffer instead),
    // but keep the cleanup hook in place so a future swap to createObjectURL
    // can revoke without forgetting.
    let createdBlobUrl: string | null = null;
    // The loading task is cancellable; abort on unmount to avoid setState on
    // a dead component if a large PDF is still parsing.
    let loadingTask: { destroy: () => Promise<void> } | null = null;

    async function render() {
      try {
        const pdfjs = await import('pdfjs-dist');

        // One-time worker setup. The bundled-worker pattern: build the worker
        // URL relative to this module via import.meta.url, then point pdfjs at
        // it. Vite picks up the new URL() call at build time and emits the
        // worker as a hashed asset.
        if (!workerInitialized) {
          const workerUrl = new URL(
            'pdfjs-dist/build/pdf.worker.mjs',
            import.meta.url,
          ).toString();
          pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
          workerInitialized = true;
        }

        // Normalize the source. We always end up handing pdfjs an ArrayBuffer
        // (never a URL string) because letting pdfjs fetch the URL itself
        // breaks against Firebase Storage signed URLs in the wild — we've seen
        // "failed to fetch" errors from PDFs that load fine in a browser tab.
        // By doing the fetch() ourselves first, we sidestep any CORS / range-
        // request weirdness in pdfjs's internal transport. Blob sources go
        // straight to arrayBuffer().
        let docData: ArrayBuffer;
        if (typeof source === 'string') {
          // Firebase Storage URLs come in two flavors today:
          //   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<path>?alt=media&token=…
          //   https://<bucket>.firebasestorage.app/<path>...
          // Browser `fetch()` against either of these can fail with
          // "Failed to fetch" when the bucket's CORS config doesn't whitelist
          // our origin (the default Blaze config does NOT whitelist Pages
          // domains). The Storage SDK's getBlob() uses the same auth /
          // transport channel as our uploads — no CORS preflight needed.
          // For non-Firebase URLs, fall back to direct fetch().
          const isFirebaseUrl =
            source.includes('firebasestorage.googleapis.com') ||
            source.includes('.firebasestorage.app/');
          try {
            if (isFirebaseUrl) {
              const fileRef = storageRef(storage, source);
              const blob = await getBlob(fileRef);
              docData = await blob.arrayBuffer();
            } else {
              const resp = await fetch(source);
              if (!resp.ok) {
                throw new Error(`Drawing fetch failed: ${resp.status}`);
              }
              docData = await resp.arrayBuffer();
            }
          } catch (err) {
            const msg = (err as Error).message ?? String(err);
            throw new Error(
              msg.includes('storage/')
                ? `Drawing not available — ${msg}`
                : 'Drawing not available — link may have expired or the file was removed.',
            );
          }
        } else {
          docData = await source.arrayBuffer();
        }

        loadingTask = pdfjs.getDocument({ data: docData });
        const pdf = await (loadingTask as unknown as {
          promise: Promise<{
            getPage: (n: number) => Promise<{
              getViewport: (p: { scale: number }) => {
                width: number;
                height: number;
              };
              render: (p: {
                canvas: HTMLCanvasElement;
                viewport: { width: number; height: number };
              }) => { promise: Promise<void> };
            }>;
          }>;
        }).promise;

        if (cancelled) return;

        const page = await pdf.getPage(1);
        if (cancelled) return;

        // Scale so the rendered viewport width matches the requested CSS width
        // at devicePixelRatio (sharp on retina).
        const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        const baseViewport = page.getViewport({ scale: 1 });
        const cssWidth = width;
        const cssHeight = (cssWidth * baseViewport.height) / baseViewport.width;
        const scale = (cssWidth * dpr) / baseViewport.width;
        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${cssHeight}px`;

        await page.render({ canvas, viewport }).promise;
        if (cancelled) return;

        setRenderedHeight(Math.round(cssHeight));
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[PdfThumbnail] render failed:', err);
        setStatus('error');
      }
    }

    void render();

    return () => {
      cancelled = true;
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
      void loadingTask?.destroy();
    };
  }, [source, width]);

  const isInteractive = typeof onClick === 'function';
  const wrapperClass = [
    'inline-flex flex-col items-center',
    isInteractive ? 'cursor-pointer' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={wrapperClass}
      onClick={onClick}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        isInteractive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      aria-label={label ? `PDF thumbnail: ${label}` : 'PDF thumbnail'}
    >
      <div
        className="relative overflow-hidden bg-white"
        style={{ width: `${width}px`, height: `${renderedHeight}px` }}
      >
        {/* Canvas is always mounted so the render effect has a ref target;
            we just hide it until rendering completes. */}
        <canvas
          ref={canvasRef}
          className={status === 'ready' ? 'block' : 'hidden'}
        />
        {status === 'loading' && (
          <div className="absolute inset-0 animate-pulse bg-gray-200" />
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center border border-red-400 bg-red-50 text-2xl font-bold text-red-500">
            ?
          </div>
        )}
      </div>
      {label && (
        <div
          className="mt-1 max-w-full truncate text-center text-xs text-gray-600"
          style={{ width: `${width}px` }}
          title={label}
        >
          {label}
        </div>
      )}
    </div>
  );
}
